import { ApiResponse, axiosInstance } from '@/lib/axios';
import { FetchParams, IPagination } from '@/types/general.type';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TChannel } from '../../_shared/types/channel.type';

export type TGetListIntegrationGoogleReviewResponse = ApiResponse<IPagination<TChannel>>;
export type TGetListIntegrationGoogleReviewParams = FetchParams;

export const getListIntegrationGoogleReview = async (
  params: TGetListIntegrationGoogleReviewParams
): Promise<TGetListIntegrationGoogleReviewResponse> => {
  const url = '/integration/google-review/org-channel';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY = 'get-list-integration-google-review';

export const getListIntegrationGoogleReviewQueryOptions = <TData = IPagination<TChannel>>(
  params: TGetListIntegrationGoogleReviewParams,
  select?: (data: TGetListIntegrationGoogleReviewResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_INTEGRATION_GOOGLE_REVIEW_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
      },
    ],
    queryFn: () => getListIntegrationGoogleReview(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListIntegrationGoogleReviewQuery<TData>(options: {
  params: TGetListIntegrationGoogleReviewParams;
  queryConfig?: QueryConfig<typeof getListIntegrationGoogleReviewQueryOptions<TData>>;
  select: (data: TGetListIntegrationGoogleReviewResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListIntegrationGoogleReviewQuery(options: {
  params: TGetListIntegrationGoogleReviewParams;
  queryConfig?: QueryConfig<typeof getListIntegrationGoogleReviewQueryOptions<TGetListIntegrationGoogleReviewResponse>>;
}): ReturnType<typeof useQuery<TGetListIntegrationGoogleReviewResponse>>;

// Implementation
export function useGetListIntegrationGoogleReviewQuery<TData = TGetListIntegrationGoogleReviewResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListIntegrationGoogleReviewParams;
  queryConfig?: QueryConfig<
    typeof getListIntegrationGoogleReviewQueryOptions<TData | TGetListIntegrationGoogleReviewResponse>
  >;
  select?: (data: TGetListIntegrationGoogleReviewResponse) => TData;
}) {
  return useQuery({
    ...getListIntegrationGoogleReviewQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<
      TData extends TGetListIntegrationGoogleReviewResponse ? TGetListIntegrationGoogleReviewResponse : TData
    >
  >;
}
